import { useEffect, useState } from 'react'
import { reportsApi, sellersApi } from '../../../services/api'
import ExportBar, { ExportColumn } from '../../../components/ExportBar'
import { useSortableData } from '../../../hooks/useSortableData'
import SortableTh from '../../../components/SortableTh'

interface CollectionsByCustomer {
  id: number
  name: string
  pending_amount: number
  overdue_amount: number
  pending_count: number
}

interface CollectionsReport {
  summary: {
    total_pending: number
    total_overdue: number
    customers_with_debt: number
  }
  by_customer: CollectionsByCustomer[]
}

interface Seller {
  id: number
  full_name: string
}

const COLLECTIONS_EXPORT_COLUMNS: ExportColumn<CollectionsByCustomer>[] = [
  { key: 'name', label: 'Cliente' },
  { key: 'pending_amount', label: 'Pendiente', format: (c) => `$${c.pending_amount.toFixed(2)}` },
  { key: 'overdue_amount', label: 'Vencido', format: (c) => `$${c.overdue_amount.toFixed(2)}` },
  { key: 'pending_count', label: 'Ventas pendientes', format: (c) => String(c.pending_count) },
]

/**
 * Reads the company-wide accounts-receivable snapshot from
 * GET /reports/collections -- the same endpoint the seller dashboard already
 * uses for a single seller's own book. A colaborador with only the
 * `finanzas` panel (no `vendedor`) sees the aggregate across all sellers by
 * default, with an optional seller filter for drill-down.
 */
export default function CollectionsTab() {
  const [report, setReport] = useState<CollectionsReport | null>(null)
  const [sellers, setSellers] = useState<Seller[]>([])
  const [sellerId, setSellerId] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    // GET /api/sellers is admin-only (it returns seller PII: dni, phone,
    // residency) -- a finanzas-only colaborador gets a 403 from it and must
    // not lose the collections report over that: fetch it separately so a
    // failure here only drops the seller-filter dropdown, not the whole tab.
    sellersApi
      .list()
      .then((res) => setSellers(res.data.sellers || []))
      .catch(() => setSellers([]))
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError('')
    const sellerIdNum = sellerId ? Number(sellerId) : undefined
    reportsApi
      .getCollections(sellerIdNum)
      .then((res) => {
        if (cancelled) return
        setReport(res.data)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Error fetching collections report:', err)
        setLoadError('No se pudo cargar el reporte de cobranza. Intenta recargar la página.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [sellerId])

  const { sortedData: sortedByCustomer, sortConfig, requestSort } = useSortableData(report?.by_customer || [])

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
        <p className="text-sm text-espresso-500 max-w-2xl">
          Saldos pendientes de cobro por cliente, agregados de toda la empresa. Este número refleja
          ventas registradas con estado pendiente, no un módulo de facturación separado.
        </p>
        <div>
          <label htmlFor="collections-seller-filter" className="block text-xs font-medium text-espresso-600 mb-1">
            Filtrar por vendedor
          </label>
          <select
            id="collections-seller-filter"
            value={sellerId}
            onChange={(e) => setSellerId(e.target.value)}
            className="border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
          >
            <option value="">Todos los vendedores</option>
            {sellers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.full_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">Cargando...</div>
      ) : loadError ? (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md text-sm" role="alert">
          {loadError}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-cream-50 shadow rounded-lg p-4">
              <div className="text-sm font-medium text-espresso-500">Cobranza Pendiente</div>
              <div className="mt-2 text-2xl font-bold text-terracotta-700">
                ${(report?.summary.total_pending || 0).toFixed(2)}
              </div>
            </div>
            <div className="bg-cream-50 shadow rounded-lg p-4">
              <div className="text-sm font-medium text-espresso-500">Cobranza Vencida</div>
              <div className="mt-2 text-2xl font-bold text-red-600">
                ${(report?.summary.total_overdue || 0).toFixed(2)}
              </div>
            </div>
            <div className="bg-cream-50 shadow rounded-lg p-4">
              <div className="text-sm font-medium text-espresso-500">Clientes con saldo</div>
              <div className="mt-2 text-2xl font-bold text-espresso-800">
                {report?.summary.customers_with_debt || 0}
              </div>
            </div>
          </div>

          {!report || report.by_customer.length === 0 ? (
            <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
              <p className="text-espresso-500">No hay saldos pendientes de cobro.</p>
            </div>
          ) : (
            <>
              <ExportBar
                data={report.by_customer}
                columns={COLLECTIONS_EXPORT_COLUMNS}
                filename="cobranza"
                title="Cobranza por Cliente"
              />
              <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-espresso-100">
                  <thead className="bg-cream-50">
                    <tr>
                      <SortableTh label="Cliente" sortKey="name" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableTh label="Pendiente" sortKey="pending_amount" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableTh label="Vencido" sortKey="overdue_amount" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableTh label="Ventas pendientes" sortKey="pending_count" sortConfig={sortConfig} onSort={requestSort} />
                    </tr>
                  </thead>
                  <tbody className="bg-cream-50 divide-y divide-espresso-100">
                    {sortedByCustomer.map((c) => (
                      <tr key={c.id} className="hover:bg-cream-100">
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">{c.name}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">${c.pending_amount.toFixed(2)}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-red-600">${c.overdue_amount.toFixed(2)}</td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-500">{c.pending_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
